import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useToast } from '../../context/ToastContext';
import { useI18n, getText } from '../../context/I18nContext';

/**
 * 工作流程設計器
 * 用於編輯 templates/{formType}/route.json 和 form.json
 */

// ==================== API 函數 ====================

const fetchTemplateDetail = async (formType) => {
  const res = await fetch(`/api/config/templates/${formType}`);
  if (!res.ok) throw new Error('Failed to fetch template');
  return res.json();
};

const fetchConfigFile = async (path) => {
  const res = await fetch(`/api/config/file?path=${encodeURIComponent(path)}`);
  if (!res.ok) throw new Error('Failed to fetch config');
  return res.json();
};

const saveConfigFile = async ({ path, content }) => {
  const res = await fetch(`/api/config/file?path=${encodeURIComponent(path)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content })
  });
  if (!res.ok) throw new Error('Failed to save config');
  return res.json();
};

const fetchPermissions = async () => {
  const res = await fetch('/api/config/file?path=config/permissions.json');
  if (!res.ok) throw new Error('Failed to fetch permissions');
  return res.json();
};

const fetchApprovers = async () => {
  const res = await fetch('/api/config/file?path=config/approvers.json');
  if (!res.ok) throw new Error('Failed to fetch approvers');
  return res.json();
};

const listTemplates = async () => {
  const res = await fetch('/api/config/list');
  if (!res.ok) throw new Error('Failed to list templates');
  return res.json();
};

// ==================== 子元件 ====================

// 可拖曳的步驟卡片
function SortableStepCard({ step, index, isSelected, onSelect, onDelete, roleTypes }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.stepId
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const stepTypeColors = {
    submit: 'bg-blue-100 border-blue-300 text-blue-800',
    approval: 'bg-yellow-100 border-yellow-300 text-yellow-800',
    completed: 'bg-green-100 border-green-300 text-green-800'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer ${
        isSelected ? 'ring-2 ring-blue-500 border-blue-500' : 'border-gray-200 hover:border-gray-300'
      } ${stepTypeColors[step.stepType] || 'bg-gray-50'}`}
      onClick={() => onSelect(step)}
    >
      {/* 拖曳手柄 */}
      <div {...attributes} {...listeners} className="cursor-grab p-1 text-gray-400 hover:text-gray-600">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
        </svg>
      </div>

      {/* 步驟編號 */}
      <span className="w-6 h-6 flex items-center justify-center bg-white rounded-full text-xs font-bold border">
        {step.stepId}
      </span>

      {/* 步驟資訊 */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate">{typeof step.stepName === 'object' ? getText(step.stepName) : step.stepName}</div>
        <div className="text-xs text-gray-500">
          {roleTypes[step.roleType]?.name || step.roleType}
        </div>
      </div>

      {/* 條件指示 */}
      {step.conditions?.length > 0 && (
        <span className="px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs rounded">條件</span>
      )}

      {/* 刪除按鈕 */}
      {step.stepType !== 'submit' && step.stepType !== 'completed' && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(step.stepId); }}
          className="p-1 text-red-400 hover:text-red-600"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// 步驟編輯面板
function StepEditor({ step, roleTypes, approvalModes, conditionOperators, onChange, approvers }) {
  if (!step) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p>選擇一個步驟進行編輯</p>
        </div>
      </div>
    );
  }

  const handleChange = (field, value) => {
    onChange({ ...step, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    onChange({
      ...step,
      [parent]: { ...step[parent], [field]: value }
    });
  };

  const addCondition = () => {
    const conditions = step.conditions || [];
    onChange({
      ...step,
      conditions: [...conditions, {
        conditionId: `cond_${Date.now()}`,
        field: '',
        operator: 'greaterThan',
        value: '',
        skipIfNotMet: true,
        description: ''
      }]
    });
  };

  const updateCondition = (index, updates) => {
    const conditions = [...(step.conditions || [])];
    conditions[index] = { ...conditions[index], ...updates };
    onChange({ ...step, conditions });
  };

  const removeCondition = (index) => {
    const conditions = [...(step.conditions || [])];
    conditions.splice(index, 1);
    onChange({ ...step, conditions });
  };

  return (
    <div className="space-y-6 p-4 overflow-y-auto">
      <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">編輯步驟</h3>

      {/* 基本資訊 */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">步驟名稱</label>
          <input
            type="text"
            value={step.stepName || ''}
            onChange={(e) => handleChange('stepName', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">步驟類型</label>
          <select
            value={step.stepType || 'approval'}
            onChange={(e) => handleChange('stepType', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            disabled={step.stepType === 'submit' || step.stepType === 'completed'}
          >
            <option value="submit">提交</option>
            <option value="approval">審核</option>
            <option value="completed">完成</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">角色類型</label>
          <select
            value={step.roleType || ''}
            onChange={(e) => handleChange('roleType', e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {Object.entries(roleTypes).map(([key, value]) => (
              <option key={key} value={key}>{value.name} - {value.description}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
          <textarea
            value={step.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* 審批規則（僅 approval 類型） */}
      {step.stepType === 'approval' && (
        <div className="space-y-4 border-t pt-4">
          <h4 className="font-medium text-gray-900">審批規則</h4>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">規則類型</label>
            <select
              value={step.approverRule?.type || 'role_based'}
              onChange={(e) => handleNestedChange('approverRule', 'type', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="role_based">角色審核</option>
              <option value="group_based">群組審核（並行）</option>
              <option value="dynamic">動態規則（主管鏈）</option>
            </select>
          </div>

          {(step.approverRule?.type === 'group_based' || step.approverRule?.type === 'role_based') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {step.approverRule?.type === 'group_based' ? '群組 ID' : '角色 ID'}
              </label>
              <select
                value={step.approverRule?.groupId || step.approverRule?.roleId || ''}
                onChange={(e) => {
                  const key = step.approverRule?.type === 'group_based' ? 'groupId' : 'roleId';
                  handleNestedChange('approverRule', key, e.target.value);
                }}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">選擇...</option>
                {Object.keys(approvers?.roles || {}).map(roleKey => (
                  <option key={roleKey} value={roleKey}>
                    {approvers.roles[roleKey].roleName} ({approvers.roles[roleKey].members?.length || 0} 人)
                  </option>
                ))}
              </select>
            </div>
          )}

          {step.approverRule?.type === 'group_based' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">通過模式</label>
              <select
                value={step.approverRule?.requiredApprovals || 'any'}
                onChange={(e) => handleNestedChange('approverRule', 'requiredApprovals', e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                {Object.entries(approvalModes.parallel?.options || {}).map(([key, desc]) => (
                  <option key={key} value={key}>{desc}</option>
                ))}
              </select>
            </div>
          )}

          {step.approverRule?.type === 'dynamic' && (
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
              動態規則將根據主管鏈和表單金額自動計算審批人
            </div>
          )}
        </div>
      )}

      {/* 條件設定 */}
      <div className="space-y-4 border-t pt-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">條件設定</h4>
          <button
            onClick={addCondition}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            + 新增條件
          </button>
        </div>

        {(step.conditions || []).map((cond, idx) => (
          <div key={cond.conditionId || idx} className="bg-gray-50 p-3 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">條件 {idx + 1}</span>
              <button
                onClick={() => removeCondition(idx)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                移除
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <input
                type="text"
                placeholder="欄位名稱"
                value={cond.field || ''}
                onChange={(e) => updateCondition(idx, { field: e.target.value })}
                className="px-2 py-1 border rounded text-sm"
              />
              <select
                value={cond.operator || 'greaterThan'}
                onChange={(e) => updateCondition(idx, { operator: e.target.value })}
                className="px-2 py-1 border rounded text-sm"
              >
                {Object.entries(conditionOperators).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="值"
                value={cond.value || ''}
                onChange={(e) => updateCondition(idx, { value: e.target.value })}
                className="px-2 py-1 border rounded text-sm"
              />
            </div>

            <input
              type="text"
              placeholder="條件描述"
              value={cond.description || ''}
              onChange={(e) => updateCondition(idx, { description: e.target.value })}
              className="w-full px-2 py-1 border rounded text-sm"
            />

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={cond.skipIfNotMet !== false}
                onChange={(e) => updateCondition(idx, { skipIfNotMet: e.target.checked })}
              />
              不符合時跳過此步驟
            </label>
          </div>
        ))}

        {(step.conditions || []).length === 0 && (
          <p className="text-sm text-gray-500">無條件限制，此步驟必定執行</p>
        )}
      </div>

      {/* 其他選項 */}
      <div className="space-y-4 border-t pt-4">
        <h4 className="font-medium text-gray-900">其他選項</h4>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={step.supportCounterSign || false}
            onChange={(e) => handleChange('supportCounterSign', e.target.checked)}
          />
          <span className="text-sm text-gray-700">允許加簽</span>
        </label>
      </div>
    </div>
  );
}

// 表單欄位編輯器
function FormFieldEditor({ formConfig, onChange }) {
  const [selectedField, setSelectedField] = useState(null);

  const fields = formConfig?.fields || {};
  const fieldList = Object.entries(fields);

  return (
    <div className="grid grid-cols-2 gap-4 h-full">
      {/* 欄位列表 */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-2 border-b font-medium">表單欄位</div>
        <div className="overflow-y-auto max-h-96">
          {fieldList.map(([fieldId, field]) => (
            <div
              key={fieldId}
              onClick={() => setSelectedField({ fieldId, ...field })}
              className={`px-4 py-2 border-b cursor-pointer hover:bg-gray-50 ${
                selectedField?.fieldId === fieldId ? 'bg-blue-50' : ''
              }`}
            >
              <div className="font-medium text-sm">{field.label || fieldId}</div>
              <div className="text-xs text-gray-500">{field.type} • {fieldId}</div>
            </div>
          ))}
        </div>
      </div>

      {/* 欄位編輯 */}
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-100 px-4 py-2 border-b font-medium">欄位設定</div>
        {selectedField ? (
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">欄位 ID</label>
              <input
                type="text"
                value={selectedField.fieldId}
                disabled
                className="w-full px-3 py-2 border rounded-lg bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">標籤</label>
              <input
                type="text"
                value={selectedField.label || ''}
                onChange={(e) => {
                  const newFields = { ...fields };
                  newFields[selectedField.fieldId] = { ...newFields[selectedField.fieldId], label: e.target.value };
                  onChange({ ...formConfig, fields: newFields });
                  setSelectedField({ ...selectedField, label: e.target.value });
                }}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">類型</label>
              <select
                value={selectedField.type || 'text'}
                onChange={(e) => {
                  const newFields = { ...fields };
                  newFields[selectedField.fieldId] = { ...newFields[selectedField.fieldId], type: e.target.value };
                  onChange({ ...formConfig, fields: newFields });
                  setSelectedField({ ...selectedField, type: e.target.value });
                }}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="text">文字</option>
                <option value="number">數字</option>
                <option value="date">日期</option>
                <option value="select">下拉選單</option>
                <option value="textarea">多行文字</option>
                <option value="file">檔案上傳</option>
              </select>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedField.required || false}
                onChange={(e) => {
                  const newFields = { ...fields };
                  newFields[selectedField.fieldId] = { ...newFields[selectedField.fieldId], required: e.target.checked };
                  onChange({ ...formConfig, fields: newFields });
                  setSelectedField({ ...selectedField, required: e.target.checked });
                }}
              />
              <span className="text-sm text-gray-700">必填</span>
            </label>
          </div>
        ) : (
          <div className="flex items-center justify-center h-48 text-gray-400">
            選擇一個欄位進行編輯
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== 主元件 ====================

export default function WorkflowDesigner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const formType = searchParams.get('form');
  const [activeTab, setActiveTab] = useState('workflow'); // workflow | form | preview
  const [selectedStep, setSelectedStep] = useState(null);
  const [routeConfig, setRouteConfig] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // 拖曳傳感器
  const sensors = useSensors(useSensor(PointerSensor));

  // 查詢模板列表
  const { data: templateList } = useQuery({
    queryKey: ['config-list'],
    queryFn: listTemplates
  });

  // 查詢權限配置
  const { data: permissionsData } = useQuery({
    queryKey: ['config-permissions'],
    queryFn: fetchPermissions
  });

  // 查詢審批人配置
  const { data: approversData } = useQuery({
    queryKey: ['config-approvers'],
    queryFn: fetchApprovers
  });

  // 查詢模板詳情
  const { data: templateData, isLoading } = useQuery({
    queryKey: ['template-detail', formType],
    queryFn: () => fetchTemplateDetail(formType),
    enabled: !!formType
  });

  // 初始化配置
  useEffect(() => {
    if (templateData?.data?.files) {
      const routeFile = templateData.data.files['route.json'];
      // 支援 form.json 或 form.json
      const formFile = templateData.data.files['form.json'];

      if (routeFile?.content) {
        setRouteConfig(routeFile.content);
      }
      if (formFile?.content) {
        setFormConfig(formFile.content);
      }
    }
  }, [templateData]);

  // 保存 mutation
  const saveMutation = useMutation({
    mutationFn: saveConfigFile,
    onSuccess: () => {
      queryClient.invalidateQueries(['template-detail', formType]);
      setHasChanges(false);
    }
  });

  // 提取配置
  const permissions = permissionsData?.data?.content || {};
  const approvers = approversData?.data?.content || {};
  const roleTypes = permissions.roleTypes || {};
  const approvalModes = permissions.approvalModes || {};
  const conditionOperators = permissions.conditionOperators || {};

  // 獲取可用的表單模板
  const templates = useMemo(() => {
    // API 返回格式: { data: { config: [...], templates: [...] } }
    const data = templateList?.data;
    if (!data) return [];
    
    // templates 目錄下的子目錄就是各表單類型
    const templateDirs = data.templates || [];
    return templateDirs.filter(t => t.type === 'directory').map(t => t.name);
  }, [templateList]);

  // 處理步驟拖曳結束
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id && routeConfig?.approvalFlow) {
      const oldIndex = routeConfig.approvalFlow.findIndex(s => s.stepId === active.id);
      const newIndex = routeConfig.approvalFlow.findIndex(s => s.stepId === over.id);
      
      const newFlow = arrayMove(routeConfig.approvalFlow, oldIndex, newIndex);
      // 重新編號
      const renumberedFlow = newFlow.map((step, idx) => ({ ...step, stepId: idx + 1 }));
      
      setRouteConfig({ ...routeConfig, approvalFlow: renumberedFlow });
      setHasChanges(true);
    }
  }, [routeConfig]);

  // 新增步驟
  const addStep = useCallback(() => {
    if (!routeConfig) return;
    
    const flow = routeConfig.approvalFlow || [];
    const completedStep = flow.find(s => s.stepType === 'completed');
    const insertIndex = completedStep ? flow.indexOf(completedStep) : flow.length;
    
    const newStep = {
      stepId: flow.length + 1,
      stepName: '新審核步驟',
      stepType: 'approval',
      roleType: 'finance',
      description: '',
      approverRule: { type: 'role_based' },
      actions: [
        { actionId: 'approve', actionName: '核准', actionType: 'approve', nextStep: insertIndex + 2 },
        { actionId: 'reject', actionName: '退回', actionType: 'reject', nextStep: 1 }
      ]
    };

    const newFlow = [...flow];
    newFlow.splice(insertIndex, 0, newStep);
    
    // 重新編號
    const renumberedFlow = newFlow.map((step, idx) => ({ ...step, stepId: idx + 1 }));
    
    setRouteConfig({ ...routeConfig, approvalFlow: renumberedFlow });
    setSelectedStep(newStep);
    setHasChanges(true);
  }, [routeConfig]);

  // 刪除步驟
  const deleteStep = useCallback((stepId) => {
    if (!routeConfig?.approvalFlow) return;
    
    const newFlow = routeConfig.approvalFlow.filter(s => s.stepId !== stepId);
    // 重新編號
    const renumberedFlow = newFlow.map((step, idx) => ({ ...step, stepId: idx + 1 }));
    
    setRouteConfig({ ...routeConfig, approvalFlow: renumberedFlow });
    setSelectedStep(null);
    setHasChanges(true);
  }, [routeConfig]);

  // 更新步驟
  const updateStep = useCallback((updatedStep) => {
    if (!routeConfig?.approvalFlow) return;
    
    const newFlow = routeConfig.approvalFlow.map(s => 
      s.stepId === updatedStep.stepId ? updatedStep : s
    );
    
    setRouteConfig({ ...routeConfig, approvalFlow: newFlow });
    setSelectedStep(updatedStep);
    setHasChanges(true);
  }, [routeConfig]);

  // 保存
  const handleSave = async () => {
    if (!formType) return;

    try {
      // 保存 route.json
      if (routeConfig) {
        await saveMutation.mutateAsync({
          path: `templates/${formType}/route.json`,
          content: routeConfig
        });
      }

      // 保存 form.json（檢查原始檔案名稱）
      if (formConfig) {
        // 判斷使用哪個檔案名稱
        const formFileName = templateData?.data?.files['form.json'] ? 'form.json' : 'form.json';
        await saveMutation.mutateAsync({
          path: `templates/${formType}/${formFileName}`,
          content: formConfig
        });
      }

      toast.success('保存成功！');
    } catch (err) {
      toast.error('保存失敗：' + err.message);
    }
  };

  // 未選擇表單
  if (!formType) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          {/* Header with navigation */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <span className="mr-2">🔧</span>
                Workflow Designer
              </h1>
              <p className="text-gray-600 mt-1">Design approval flows and form structures</p>
            </div>
            <div className="flex items-center gap-2">
              <Link 
                to="/dev" 
                className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                🛠️ Dev Panel
              </Link>
              <Link 
                to="/config" 
                className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                ⚙️ Config Manager
              </Link>
            </div>
          </div>

          {/* Quick Help */}
          <div className="mb-6 bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">📖 How to Use</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-700">
              <div className="flex items-start gap-2">
                <span className="bg-blue-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">1</span>
                <div>
                  <p className="font-medium">Select a form type</p>
                  <p className="text-xs text-blue-600">Choose from the list below to edit its workflow</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-blue-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">2</span>
                <div>
                  <p className="font-medium">Edit approval steps</p>
                  <p className="text-xs text-blue-600">Add, remove, or reorder approval steps</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-blue-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">3</span>
                <div>
                  <p className="font-medium">Set conditions</p>
                  <p className="text-xs text-blue-600">Define when each step should be triggered</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Template Selection */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Select Form Type to Edit</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <button
                  key={template}
                  onClick={() => navigate(`?form=${template}`)}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-2xl">📋</span>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 group-hover:bg-blue-100 group-hover:text-blue-700">
                      Edit →
                    </span>
                  </div>
                  <div className="font-medium text-gray-900">{template}</div>
                  <div className="text-sm text-gray-500">Click to edit workflow</div>
                </button>
              ))}
            </div>

            {templates.length === 0 && (
              <div className="text-center py-8">
                <p className="text-4xl mb-2">📭</p>
                <p className="text-gray-500 mb-4">No form templates available</p>
                <Link 
                  to="/config"
                  className="text-blue-600 hover:underline"
                >
                  Go to Config Manager to create templates →
                </Link>
              </div>
            )}
          </div>

          {/* File Structure Reference */}
          <div className="mt-6 bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">📁 Template File Structure</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <code className="text-blue-600">templates/[form-type]/route.json</code>
                <p className="text-gray-500 text-xs mt-1">Defines approval workflow steps, conditions, and actions</p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <code className="text-blue-600">templates/[form-type]/form.json</code>
                <p className="text-gray-500 text-xs mt-1">Defines form fields, types, validation rules</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">載入中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 頂部工具列 */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/workflow-designer')}
              className="text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm">Back</span>
            </button>
            <div className="border-l border-gray-200 pl-4">
              <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <span>📋</span>
                {formType}
              </h1>
              <p className="text-xs text-gray-500">Workflow Designer</p>
            </div>
            {hasChanges && (
              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded font-medium">
                ● Unsaved changes
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Quick Links */}
            <div className="hidden md:flex items-center gap-2 text-sm">
              <Link to="/config" className="text-gray-500 hover:text-gray-700">⚙️ Config</Link>
              <span className="text-gray-300">|</span>
              <Link to="/dev" className="text-gray-500 hover:text-gray-700">🛠️ Dev</Link>
            </div>

            {/* Tab 切換 */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('workflow')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'workflow' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                🔄 Workflow
              </button>
              <button
                onClick={() => setActiveTab('form')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'form' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                📝 Form Fields
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'preview' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {'{}'} JSON
              </button>
            </div>

            <button
              onClick={handleSave}
              disabled={!hasChanges || saveMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <span>{saveMutation.isPending ? '⏳' : '💾'}</span>
              {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* 主要內容區 */}
      <div className="max-w-7xl mx-auto p-4">
        {activeTab === 'workflow' && (
          <div className="grid grid-cols-3 gap-4 h-[calc(100vh-140px)]">
            {/* 左側：步驟列表 */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
              <div className="bg-gray-100 px-4 py-3 border-b flex items-center justify-between">
                <h2 className="font-medium text-gray-900">Approval Steps</h2>
                <button
                  onClick={addStep}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <span>+</span> Add Step
                </button>
              </div>
              
              {/* Usage Tips */}
              <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-700">
                💡 Drag steps to reorder • Click to edit • Delete unused steps
              </div>
              
              <div className="p-4 overflow-y-auto flex-1">
                {routeConfig?.approvalFlow && (
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext
                      items={routeConfig.approvalFlow.map(s => s.stepId)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-2">
                        {routeConfig.approvalFlow.map((step, index) => (
                          <SortableStepCard
                            key={step.stepId}
                            step={step}
                            index={index}
                            isSelected={selectedStep?.stepId === step.stepId}
                            onSelect={setSelectedStep}
                            onDelete={deleteStep}
                            roleTypes={roleTypes}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}
                
                {(!routeConfig?.approvalFlow || routeConfig.approvalFlow.length === 0) && (
                  <div className="text-center py-8 text-gray-400">
                    <p className="text-2xl mb-2">📭</p>
                    <p>No steps defined</p>
                    <button onClick={addStep} className="mt-2 text-blue-600 text-sm hover:underline">
                      + Add your first step
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* 右側：步驟編輯 */}
            <div className="col-span-2 bg-white rounded-xl shadow-sm overflow-hidden">
              <StepEditor
                step={selectedStep}
                roleTypes={roleTypes}
                approvalModes={approvalModes}
                conditionOperators={conditionOperators}
                onChange={updateStep}
                approvers={approvers}
              />
            </div>
          </div>
        )}

        {activeTab === 'form' && (
          <div className="bg-white rounded-xl shadow-sm p-6 h-[calc(100vh-140px)]">
            <div className="mb-4 bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-sm text-blue-700">
                💡 <strong>Tip:</strong> Edit form fields here. Changes will be saved to <code className="bg-blue-100 px-1 rounded">form.json</code>
              </p>
            </div>
            <FormFieldEditor
              formConfig={formConfig}
              onChange={(newConfig) => {
                setFormConfig(newConfig);
                setHasChanges(true);
              }}
            />
          </div>
        )}

        {activeTab === 'preview' && (
          <div className="space-y-4">
            <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Raw JSON Preview</strong> - For advanced users. Edit with caution.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 h-[calc(100vh-200px)]">
              <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
                <div className="bg-gray-100 px-4 py-3 border-b font-medium flex items-center justify-between">
                  <span>route.json</span>
                  <span className="text-xs text-gray-500">Approval workflow definition</span>
                </div>
                <pre className="p-4 text-xs overflow-auto flex-1 bg-gray-900 text-green-400">
                  {JSON.stringify(routeConfig, null, 2)}
                </pre>
              </div>
              <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
                <div className="bg-gray-100 px-4 py-3 border-b font-medium flex items-center justify-between">
                  <span>form.json</span>
                  <span className="text-xs text-gray-500">Form field definitions</span>
                </div>
                <pre className="p-4 text-xs overflow-auto flex-1 bg-gray-900 text-green-400">
                  {JSON.stringify(formConfig, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
